import React, { useLayoutEffect, useEffect, useRef, useState } from 'react'
import { FaCamera, FaCheck, FaClock, FaPaperPlane } from 'react-icons/fa'
import { CHAT_TOPICS } from '../demoData'
import { clockOfStamp } from '../geo'
import { fmtDate, isoDate } from '../FieldOpsKit'

/**
 * A conversation between a salesperson / driver and the office. Used by the worker app (side 'worker') and by the
 * administrators' inbox (side 'manager'). Messages: from 'worker' | 'manager' | 'system' (automatic notices such as
 * "Return RT-105 requested"). Offline messages show a clock until they are sent.
 *
 * The component fills the height of its parent (the parent decides it): the message list scrolls on its own and opens
 * on the newest message; the composer stays pinned at the bottom. Phone: topic select, quick replies, a full-width
 * text box and, below it, photo + send. Wide screens: select + quick replies on one line, photo + text + send on another.
 */
const TOPIC_LABEL = {}
CHAT_TOPICS.forEach((t) => { TOPIC_LABEL[t.key] = t.label })

export default function ChatThread({ messages, side, meta, quick, onSend, placeholder, readOnly }) {
    const [text, setText] = useState('')
    const [topic, setTopic] = useState('general')
    const [photo, setPhoto] = useState(false)
    const listRef = useRef(null)
    const readMark = side === 'worker' ? (meta ? meta.readByManager : 0) : (meta ? meta.readByWorker : 0)

    // always show the newest message: on open and whenever one arrives
    const toBottom = () => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight }
    useLayoutEffect(() => { toBottom() }, [messages.length])
    useEffect(() => { const t = [setTimeout(toBottom, 60), setTimeout(toBottom, 400)]; return () => t.forEach(clearTimeout) }, [])

    const send = () => {
        if (!text.trim() && !photo) return
        onSend({ text: text.trim() || '(photo)', topic: topic, ref: null, photo: photo })
        setText(''); setPhoto(false); setTopic('general')
    }

    let lastDay = ''
    return (
        <div className="fo-chat">
            <div className="fo-chat__list" ref={listRef}>
                {messages.length === 0 ? <div className="fo-muted" style={{ textAlign: 'center', padding: 30 }}>No messages yet. Write to the office about a change, complaint, return or anything you need.</div> : null}
                {messages.map((m) => {
                    const day = isoDate(new Date(m.at))
                    const showDay = day !== lastDay
                    lastDay = day
                    const mine = m.from === side
                    return (
                        <React.Fragment key={m.id}>
                            {showDay ? <div className="fo-chat__day"><span>{fmtDate(day)}</span></div> : null}
                            {m.from === 'system' ? (
                                <div className="fo-chat__sys"><span>{m.text}</span><small>{clockOfStamp(m.at)}{m.ref ? ' · ' + m.ref.label : ''}</small></div>
                            ) : (
                                <div className={'fo-bubble ' + (mine ? 'fo-bubble--me' : 'fo-bubble--them')}>
                                    {!mine ? <div className="fo-bubble__by">{m.by}</div> : null}
                                    {m.topic && m.topic !== 'general' ? <div className="fo-bubble__topic">{TOPIC_LABEL[m.topic]}</div> : null}
                                    {m.ref ? <div className="fo-bubble__ref">{m.ref.label}</div> : null}
                                    {m.photo ? <div className="fo-bubble__photo"><FaCamera /> Photo attached</div> : null}
                                    <div className="fo-bubble__text">{m.text}</div>
                                    <div className="fo-bubble__meta">{clockOfStamp(m.at)}{mine ? (m.pending ? <span title="Waiting for signal"> <FaClock /> waiting for signal</span> : <span className={m.at <= readMark ? 'is-read' : ''}> <FaCheck />{m.at <= readMark ? <FaCheck style={{ marginLeft: -6 }} /> : null}</span>) : null}</div>
                                </div>
                            )}
                        </React.Fragment>
                    )
                })}
            </div>
            {readOnly ? null : (
                <div className="fo-chat__compose">
                    <div className="fo-chat__top">
                        <select className="fo-chat__topic" value={topic} onChange={(e) => setTopic(e.target.value)} aria-label="Topic">{CHAT_TOPICS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select>
                        {quick && quick.length ? <div className="fo-chat__quick">{quick.map((q) => <button key={q.text} type="button" onClick={() => { setText(q.text); setTopic(q.topic) }}>{q.text}</button>)}</div> : null}
                    </div>
                    <div className="fo-chat__row">
                        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder || 'Write a message'} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }} aria-label="Message" />
                        <button type="button" className={'fo-chat__attach' + (photo ? ' is-on' : '')} onClick={() => setPhoto(!photo)} aria-label="Attach a photo" title="Attach a photo"><FaCamera /><span>{photo ? 'Photo added' : 'Photo'}</span></button>
                        <button type="button" className="fo-chat__send" onClick={send} disabled={!text.trim() && !photo} aria-label="Send"><FaPaperPlane /><span>Send</span></button>
                    </div>
                </div>
            )}
        </div>
    )
}
